import {
  Box,
  Divider,
  Flex,
  Heading,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";

import {
  Button,
  ButtonWithConfirmDialogue,
  Select,
  useToast,
} from "../../../../../components";
import { useUpdatePositionsRecordingSettingsByBulkMutation } from "../../../../graphql";

interface RecordingSettingsPopoverProps {
  searchTerm: string | null;
  locationId: string | null;
  clientId: string | null;
  positionsCount: number;
  disabled?: boolean;
  showOptIn: boolean;
  isInRow?: boolean;
  onSettingsUpdated?: () => void;
}

const RecordingSettingsPopover: React.FC<RecordingSettingsPopoverProps> = ({
  searchTerm = null,
  locationId = null,
  clientId = null,
  positionsCount,
  disabled,
  showOptIn,
  isInRow = true,
  onSettingsUpdated = null,
}) => {
  const toast = useToast();
  const [selectedAutoJoinOption, setSelectedAutoJoinOption] = useState<{
    value: boolean;
    label: string;
  } | null>(null);
  const [selectedOptInOption, setSelectedOptInOption] = useState<{
    value: boolean;
    label: string;
  } | null>(null);

  const [updateRecordingSettings, { loading }] =
    useUpdatePositionsRecordingSettingsByBulkMutation({
      onError: (err) => {
        toast({
          status: "error",
          title: "Error",
          description: `Failed to update setting for positions: ${err.message}`,
        });
      },
      onCompleted: (data, options) => {
        const vars = options?.variables;
        toast({
          title: "Success",
          description: `Turned ${
            vars?.autoJoinInterview || vars?.atsOptInInterview
              ? '"on"'
              : '"off"'
          } for ${
            data.updatePositionsRecordingSettingsByBulk?.positions.length || 0
          } positions`,
          status: "success",
        });

        if (onSettingsUpdated) {
          onSettingsUpdated();
        }
      },
    });

  const handleUpdateOrgSettings = (): void => {
    updateRecordingSettings({
      variables: {
        locationId,
        clientId,
        autoJoinInterview: selectedAutoJoinOption?.value,
        atsOptInInterview: selectedOptInOption?.value,
        query: searchTerm ?? "",
      },
    });
  };

  const singularPluralPosition = positionsCount > 1 ? "positions" : "position";
  const singularPluralThis = positionsCount > 1 ? "these" : "this";

  const { onOpen, onClose, isOpen } = useDisclosure();
  const [closeOnBlur, setCloseOnBlur] = useState(true);

  const popoverBodyHeight = showOptIn ? "250px" : "175px";
  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      gutter={40}
      closeOnBlur={closeOnBlur}
    >
      <PopoverTrigger>
        <Button
          disabled={disabled}
          ml={isInRow ? "0" : "auto"}
          size="sm"
          variant="outline"
          fontSize="14px"
          fontWeight="normal"
          textTransform="none"
          colorScheme="blue"
          rightIcon={
            <Icon
              as={isOpen ? IoChevronUp : IoChevronDown}
              boxSize="4"
              marginLeft="4"
              display={{ base: "none", lg: "block" }}
            />
          }
        >
          Bulk Edit All Filtered Positions
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          maxH="480"
          minW="680"
          overflowY="scroll"
          pl="3"
          pr="3"
          pt="4"
          boxShadow="elevate"
          _focus={{}}
        >
          <PopoverBody height={popoverBodyHeight}>
            <Heading as="h2" size="sm" mb={2}>
              Bulk edit all filtered positions
            </Heading>
            <>
              {showOptIn && (
                <Box mb="6" key="opt-in-bulk">
                  <Flex
                    flexFlow="row nowrap"
                    justifyContent="flex-start"
                    mb="2"
                  >
                    <Select
                      ref={null}
                      placeholder="Choose consent type"
                      styles={{
                        container: (base: Record<string, unknown>) => ({
                          ...base,
                          width: 250,
                          maxHeight: 38,
                        }),
                      }}
                      isClearable={false}
                      options={[
                        { value: true, label: "Opt-In Consent" },
                        { value: false, label: "Opt-Out Consent" },
                      ]}
                      value={selectedOptInOption}
                      onChange={setSelectedOptInOption}
                    />
                    {selectedOptInOption?.value ? (
                      <ButtonWithConfirmDialogue
                        buttonProps={{
                          size: "sm",
                          ml: "2",
                          variant: "outline",
                          colorScheme: "blue",
                          fontSize: "14px",
                          textTransform: "none",
                          width: "200px",
                          height: "38px",
                          isLoading: loading,
                        }}
                        onConfirm={() => {
                          handleUpdateOrgSettings();
                        }}
                        headerText={`Are you sure you want to enable opt-in consent for ${positionsCount} ${singularPluralPosition}?`}
                        headerBody={
                          <>
                            Enabling <strong>Opt In Consent</strong> for{" "}
                            {singularPluralThis} {positionsCount}{" "}
                            {singularPluralPosition} will immediately apply
                            opt-in rules to all active candidates in{" "}
                            {singularPluralThis} {singularPluralPosition}. Any
                            candidate not currently assigned to an opt-in
                            position will be automatically marked as Opted Out,
                            which may disrupt scheduling and generate a large
                            volume of opt-out notification emails. This change
                            may be difficult to reverse.
                          </>
                        }
                        confirmButtonText="Proceed Anyways"
                        additionalOnClick={() => {
                          setCloseOnBlur(false);
                        }}
                        additionalOnClose={() => {
                          setCloseOnBlur(true);
                        }}
                      >
                        Set for {positionsCount} {singularPluralPosition}
                      </ButtonWithConfirmDialogue>
                    ) : (
                      <Button
                        size="sm"
                        ml="2"
                        variant="outline"
                        colorScheme="blue"
                        fontSize="14px"
                        textTransform="none"
                        width="200px"
                        height="38px"
                        onClick={() => handleUpdateOrgSettings()}
                        isLoading={loading}
                        disabled={!selectedOptInOption}
                      >
                        {positionsCount === 1
                          ? `Set for 1 position`
                          : `Set for all ${positionsCount} positions`}
                      </Button>
                    )}
                  </Flex>
                  <Divider />
                </Box>
              )}
              <Box mb="6" key="auto-join-bulk">
                <Flex flexFlow="row nowrap" justifyContent="flex-start" mb="2">
                  <Select
                    ref={null}
                    placeholder="Choose auto join setting"
                    styles={{
                      container: (base: Record<string, unknown>) => ({
                        ...base,
                        width: 250,
                        maxHeight: 38,
                      }),
                    }}
                    isClearable={false}
                    options={[
                      { value: true, label: "Auto Join On" },
                      { value: false, label: "Auto Join Off" },
                    ]}
                    value={selectedAutoJoinOption}
                    onChange={setSelectedAutoJoinOption}
                  />
                  <Button
                    size="sm"
                    ml="2"
                    variant="outline"
                    colorScheme="blue"
                    fontSize="14px"
                    textTransform="none"
                    width="200px"
                    height="38px"
                    onClick={() => handleUpdateOrgSettings()}
                    isLoading={loading}
                    disabled={!selectedAutoJoinOption}
                  >
                    {positionsCount === 1
                      ? `Set for 1 position`
                      : `Set for all ${positionsCount} positions`}
                  </Button>
                </Flex>
                <Divider />
              </Box>
            </>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default RecordingSettingsPopover;
